import React from 'react'
import { Route, Navigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import LoadingPage from '../Components/LoadingPage'

// ProtectedRoute: redirects to landing page if not authenticated
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth0()

  if (isLoading) {
    return <LoadingPage />
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />
  }

  return children
}

// Lazy-loaded components
const LazyLandingPage = React.lazy(() => import('../Containers/LandingPageContainer'))
const LazyHomeToolkit = React.lazy(() => import('../Components/HomeToolkit/HomeToolkit'))
const LazyMyLoanContainer = React.lazy(() => import('../Containers/MyLoanContainer'))
const LazyCommonForms = React.lazy(() => import('../Containers/CommonFormsContainer'))
const LazyCommonQuestions = React.lazy(() => import('../Components/CommonQuestions'))
const LazyContact = React.lazy(() => import('../Components/Contact'))
const LazyWiringInstructions = React.lazy(() => import('../Components/WiringInstructions'))
const LazyPrivacyPolicy = React.lazy(() => import('../Components/PrivacyPolicy'))
const LazyTermsConditions = React.lazy(() => import('../Components/TermsAndConditions'))
const LazyUpload = React.lazy(() => import('../Components/Upload/index'))
const LazyScheduleOfFees = React.lazy(() => import('../Components/ScheduleOfFees'))
const LazyLicenses = React.lazy(() => import('../Components/Licenses'))
const LazyAccessibilityStatement = React.lazy(() => import('../Components/AccessibilityStatement'))
const LazyAdvanceRequestContainer = React.lazy(() => import('../Containers/AdvanceRequestContainer'))
const LazyMyAdvanceRequestsContainer = React.lazy(() => import('../Containers/MyAdvanceRequestsContainer'))
const LazyPayoffQuote = React.lazy(() => import('../Containers/PayoffQuoteContainer'))
const LazyImportantDisclosures = React.lazy(() => import('../Components/ImportantDisclosures'))
const LazyAccountSettings = React.lazy(() => import('../Containers/AccountSettings'))
const LazyFinishYourAccountContainer = React.lazy(() => import('../Containers/FinishYourAccountContainer'))
const LazyTransactions = React.lazy(() => import('../Components/Transactions/index'))
const LazyStatementsAndDocuments = React.lazy(() => import('../Components/StatementsAndDocuments/index'))
const LazyAnnualPrivacyNotice = React.lazy(() => import('../Components/AnnualPrivacyNotice/index'))
const LazyClaim = React.lazy(() => import('../Components/Claim'))
const LazyESignContainer = React.lazy(() => import('../Containers/ESignContainer'))
const LazyHelpfulResourcesContainer = React.lazy(() => import('../Containers/HelpfulResourcesContainer'))
const LazyEnableMultifactorContainer = React.lazy(() => import('../Containers/EnableMultifactorContainer'))
const LazyPaymentContainer = React.lazy(() => import('../Containers/PaymentContainer'))

// Paths that require authentication
const protectedPaths = new Set([
  '/my-loan',
  '/transactions',
  '/statements-and-documents',
  '/annual-privacy-notice',
  '/account-settings',
  '/account-settings/enable-multifactor',
  '/advance-request',
  '/my-advance-requests',
  '/payoff-quote',
  '/upload',
  '/esign',
  '/enable-multifactor',
  '/finish-your-account',
])

export const RouteList = [
  { path: '/', element: <LazyLandingPage /> },
  { path: '/paperless', element: <LazyLandingPage /> },
  { path: '/home-toolkit', element: <LazyHomeToolkit /> },
  { path: '/helpful-resources', element: <LazyHelpfulResourcesContainer /> },
  { path: '/common-questions', element: <LazyCommonQuestions /> },
  { path: '/common-forms', element: <LazyCommonForms /> },
  { path: '/contact', element: <LazyContact /> },
  { path: '/wiring-instructions', element: <LazyWiringInstructions /> },
  { path: '/confirm-wiring', element: <LazyWiringInstructions /> },
  { path: '/payment', element: <LazyPaymentContainer /> },
  { path: '/privacy-policy', element: <LazyPrivacyPolicy /> },
  { path: '/terms-conditions', element: <LazyTermsConditions /> },
  { path: '/schedule-of-borrower-fees', element: <LazyScheduleOfFees /> },
  { path: '/licenses', element: <LazyLicenses /> },
  { path: '/important-disclosures', element: <LazyImportantDisclosures /> },
  { path: '/claims', element: <LazyClaim /> },
  { path: '/accessibility-statement', element: <LazyAccessibilityStatement /> },
  // Protected routes
  { path: '/my-loan', element: <LazyMyLoanContainer /> },
  { path: '/transactions', element: <LazyTransactions /> },
  { path: '/statements-and-documents', element: <LazyStatementsAndDocuments /> },
  { path: '/annual-privacy-notice', element: <LazyAnnualPrivacyNotice /> },
  { path: '/account-settings', element: <LazyAccountSettings /> },
  { path: '/account-settings/enable-multifactor', element: <LazyEnableMultifactorContainer /> },
  { path: '/advance-request', element: <LazyAdvanceRequestContainer /> },
  { path: '/my-advance-requests', element: <LazyMyAdvanceRequestsContainer /> },
  { path: '/payoff-quote', element: <LazyPayoffQuote /> },
  { path: '/upload', element: <LazyUpload /> },
  { path: '/esign', element: <LazyESignContainer /> },
  { path: '/enable-multifactor', element: <LazyEnableMultifactorContainer /> },
  { path: '/finish-your-account', element: <LazyFinishYourAccountContainer /> },
]

export const Routes = () => (
  <>
    {RouteList.map(({ path, element }) => {
      const isProtected = protectedPaths.has(path)

      return (
        <Route
          key={path}
          path={path}
          element={
            <React.Suspense fallback={<LoadingPage />}>
              {isProtected ? (
                <ProtectedRoute>{element}</ProtectedRoute>
              ) : (
                element
              )}
            </React.Suspense>
          }
        />
      )
    })}
  </>
)